import { useEffect, useState } from "react";
import { FileBarChart2, Download, FileSpreadsheet, FileText, Loader2, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useActiveCampaign } from "@/contexts/ActiveCampaignContext";

// Each report descriptor: name, slug, scope, icon, desc
const REPORTS = [
  {
    slug: "campaign-summary",
    name: "Campaign Summary",
    desc: "Campaign details, KPIs, scenario, warnings.",
    icon: FileBarChart2,
    scope: "campaign",
  },
  {
    slug: "optimizer-result",
    name: "Optimizer Result",
    desc: "All saved scenarios for the campaign compared side-by-side.",
    icon: FileText,
    scope: "campaign",
  },
  {
    slug: "budget-sensitivity",
    name: "Budget Sensitivity",
    desc: "Reach vs budget elasticity computed live across 11 points.",
    icon: FileBarChart2,
    scope: "campaign",
  },
  {
    slug: "curve-detail",
    name: "Curve Detail",
    desc: "TV and OLV reach curve data with validation warnings.",
    icon: FileText,
    scope: "curve",
  },
  {
    slug: "vendor-parameters",
    name: "Vendor Parameters",
    desc: "All CPRP / CPM / eCPM / viewability per vendor.",
    icon: FileText,
    scope: "global",
  },
  {
    slug: "ddf-matrix",
    name: "DDF Matrix",
    desc: "De-duplication factors between touchpoints.",
    icon: FileText,
    scope: "global",
  },
];

function inferFilename(headers, fallback) {
  const cd = headers["content-disposition"] || headers["Content-Disposition"];
  if (cd) {
    const m = /filename="?([^"]+)"?/.exec(cd);
    if (m) return m[1];
  }
  return fallback;
}

export default function Reports() {
  const { campaigns, activeCampaignId, setActiveCampaignId } = useActiveCampaign();
  const [curves, setCurves] = useState([]);
  const [curveId, setCurveId] = useState("");
  const [busy, setBusy] = useState(null); // "{slug}-{fmt}"

  useEffect(() => {
    api.get("/curve-sets").then(({ data }) => {
      setCurves(data);
      if (data.length && !curveId) setCurveId(data[0].id);
    });
    // Run once on mount to populate the curve list. `curveId` is intentionally
    // omitted — including it would re-fire whenever the user picks a curve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportReport = async (report, fmt) => {
    const key = `${report.slug}-${fmt}`;
    setBusy(key);
    try {
      const params = { format: fmt };
      if (report.scope === "campaign") {
        if (!activeCampaignId) {
          toast.error("Please select an active campaign first.");
          setBusy(null);
          return;
        }
        params.campaign_id = activeCampaignId;
      } else if (report.scope === "curve") {
        if (!curveId) {
          toast.error("Please select a curve set first.");
          setBusy(null);
          return;
        }
        params.curve_set_id = curveId;
      }
      const res = await api.get(`/reports/${report.slug}`, {
        params,
        responseType: "blob",
      });
      const filename = inferFilename(res.headers, `${report.slug}.${fmt}`);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${report.name} (${fmt.toUpperCase()}) downloaded`);
    } catch (err) {
      // err.response.data is a Blob — read it for the JSON detail
      try {
        const text = await err.response.data.text();
        const j = JSON.parse(text);
        toast.error(j.detail || formatApiError(err));
      } catch {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(null);
    }
  };

  const activeCampaign = campaigns.find((c) => c.id === activeCampaignId);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900">Reports</h2>
        <p className="text-sm text-slate-500 mt-1">
          Export branded HARMONY reports in PDF or Excel format.
        </p>
      </div>

      {/* Context selector */}
      <div className="card-harmony p-5">
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Report Context</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Active Campaign <span className="text-slate-400">(for campaign-scoped reports)</span>
            </label>
            <Select value={activeCampaignId} onValueChange={(v) => setActiveCampaignId(v)}>
              <SelectTrigger className="mt-2" data-testid="reports-campaign-select">
                <SelectValue placeholder="Select campaign…" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.campaign_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeCampaign && (
              <div className="text-xs text-slate-500 mt-1">
                {activeCampaign.client} · {activeCampaign.target_group}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Curve Set <span className="text-slate-400">(for the Curve Detail report)</span>
            </label>
            <Select value={curveId} onValueChange={setCurveId}>
              <SelectTrigger className="mt-2" data-testid="reports-curve-select">
                <SelectValue placeholder="Select curve set…" />
              </SelectTrigger>
              <SelectContent>
                {curves.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.curve_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!activeCampaignId && (
          <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Campaign-scoped reports (first three below) require an active campaign.
          </div>
        )}
      </div>

      {/* Report cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {REPORTS.map((r) => {
          const scopeMissing =
            (r.scope === "campaign" && !activeCampaignId) ||
            (r.scope === "curve" && !curveId);
          const pdfKey = `${r.slug}-pdf`;
          const xlsxKey = `${r.slug}-xlsx`;
          return (
            <div key={r.slug} className="card-harmony p-5 flex flex-col" data-testid={`report-${r.slug}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="h-10 w-10 rounded-lg bg-[#0A3D91]/8 text-[#0A3D91] flex items-center justify-center">
                  <r.icon className="h-5 w-5" />
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${
                  r.scope === "campaign" ? "bg-[#0E73C9]/10 text-[#0E73C9]" :
                  r.scope === "curve" ? "bg-[#18B7D8]/15 text-[#0E73C9]" :
                  "bg-slate-100 text-slate-600"
                }`}>
                  {r.scope}
                </span>
              </div>
              <h3 className="font-display font-semibold text-slate-900">{r.name}</h3>
              <p className="text-sm text-slate-500 mt-1 flex-1">{r.desc}</p>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => exportReport(r, "pdf")}
                  disabled={!!busy || scopeMissing}
                  className="btn-secondary flex-1 justify-center !py-2 text-xs"
                  data-testid={`report-${r.slug}-pdf-btn`}
                >
                  {busy === pdfKey ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileText className="h-3.5 w-3.5" />
                  )}
                  PDF
                </button>
                <button
                  onClick={() => exportReport(r, "xlsx")}
                  disabled={!!busy || scopeMissing}
                  className="btn-secondary flex-1 justify-center !py-2 text-xs"
                  data-testid={`report-${r.slug}-xlsx-btn`}
                >
                  {busy === xlsxKey ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                  )}
                  Excel
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card-harmony p-5">
        <div className="flex items-start gap-2 text-xs text-slate-600 p-3 bg-slate-50 rounded-lg">
          <Download className="h-4 w-4 text-[#0E73C9] mt-0.5" />
          <div>
            All reports include the HARMONY brand header, generated timestamp, and clean tabular data.
            PDF reports are landscape A4 where applicable. Excel reports use the same column layout for downstream use.
          </div>
        </div>
      </div>
    </div>
  );
}
